<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';

const ACCEPTED_FILE_TYPES =
  'image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.json,.xml';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    existingAttachments: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      attachedFiles: [],
      removedAttachmentIds: [],
      show: true,
    };
  },
  computed: {
    acceptedFileTypes() {
      return ACCEPTED_FILE_TYPES;
    },
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    currentAttachments() {
      return this.existingAttachments.filter(
        a => !this.removedAttachmentIds.includes(a.id)
      );
    },
    isFormValid() {
      const hasContent = !this.v$.content.$invalid;
      const hasShortCode = !this.v$.shortCode.$invalid;
      const hasAttachments =
        this.attachedFiles.length > 0 || this.currentAttachments.length > 0;
      return hasShortCode && (hasContent || hasAttachments);
    },
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.attachedFiles = [];
      this.removedAttachmentIds = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    onFileChange(event) {
      const files = Array.from(event.target.files);
      files.forEach(file => {
        this.attachedFiles.push({
          file,
          name: file.name,
          type: file.type,
          size: file.size,
          preview: file.type.startsWith('image/')
            ? URL.createObjectURL(file)
            : null,
        });
      });
      event.target.value = '';
    },
    removeNewFile(index) {
      const removed = this.attachedFiles.splice(index, 1);
      if (removed[0]?.preview) {
        URL.revokeObjectURL(removed[0].preview);
      }
    },
    removeExistingAttachment(attachmentId) {
      this.removedAttachmentIds.push(attachmentId);
    },
    getFileIcon(type) {
      if (type.startsWith('image/') || type.startsWith('image')) return '🖼️';
      if (type.startsWith('audio/') || type.startsWith('audio')) return '🎵';
      if (type.startsWith('video/') || type.startsWith('video')) return '🎬';
      return '📄';
    },
    formatFileSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    buildFormData() {
      const formData = new FormData();
      formData.append('canned_response[short_code]', this.shortCode);
      formData.append('canned_response[content]', this.content);
      this.attachedFiles.forEach(({ file }) => {
        formData.append('canned_response[attachments][]', file);
      });
      return formData;
    },
    editCannedResponse() {
      this.editCanned.showLoading = true;
      const hasNewFiles = this.attachedFiles.length > 0;
      const payload = hasNewFiles
        ? this.buildFormData()
        : { short_code: this.shortCode, content: this.content };

      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          ...(!hasNewFiles ? payload : {}),
          ...(hasNewFiles ? { formData: payload } : {}),
        })
        .then(() => {
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              show-source-toggle
              override-line-breaks
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <!-- Attachment section -->
        <div class="w-full mb-4">
          <label
            class="flex items-center gap-2 mb-2 text-sm font-medium text-n-slate-11"
          >
            {{ $t('CANNED_MGMT.EDIT.FORM.ATTACHMENTS.LABEL') }}
          </label>

          <!-- Existing attachments -->
          <div
            v-if="currentAttachments.length"
            class="flex flex-wrap gap-2 mb-3"
          >
            <div
              v-for="attachment in currentAttachments"
              :key="attachment.id"
              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak text-sm"
            >
              <span class="text-lg">
                {{ getFileIcon(attachment.content_type) }}
              </span>
              <div class="flex flex-col min-w-0">
                <span class="truncate max-w-[150px] text-n-slate-12">
                  {{ attachment.filename }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ formatFileSize(attachment.byte_size) }}
                </span>
              </div>
              <button
                type="button"
                class="ml-1 text-n-slate-10 hover:text-n-ruby-9 transition-colors cursor-pointer"
                @click="removeExistingAttachment(attachment.id)"
              >
                ✕
              </button>
            </div>
          </div>

          <label
            class="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-n-weak hover:border-n-slate-7 cursor-pointer transition-colors text-sm text-n-slate-10 hover:text-n-slate-12"
          >
            <span>📎</span>
            <span>{{
              $t('CANNED_MGMT.EDIT.FORM.ATTACHMENTS.PLACEHOLDER')
            }}</span>
            <input
              type="file"
              multiple
              :accept="acceptedFileTypes"
              class="hidden"
              @change="onFileChange"
            />
          </label>

          <!-- New file previews -->
          <div v-if="attachedFiles.length" class="flex flex-wrap gap-2 mt-3">
            <div
              v-for="(attachment, index) in attachedFiles"
              :key="'new-' + index"
              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak text-sm"
            >
              <img
                v-if="attachment.preview"
                :src="attachment.preview"
                class="size-8 rounded object-cover"
              />
              <span v-else class="text-lg">
                {{ getFileIcon(attachment.type) }}
              </span>
              <div class="flex flex-col min-w-0">
                <span class="truncate max-w-[150px] text-n-slate-12">
                  {{ attachment.name }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ formatFileSize(attachment.size) }}
                </span>
              </div>
              <button
                type="button"
                class="ml-1 text-n-slate-10 hover:text-n-ruby-9 transition-colors cursor-pointer"
                @click="removeNewFile(index)"
              >
                ✕
              </button>
            </div>
          </div>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="!isFormValid || editCanned.showLoading"
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(*) {
  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
